import { Injectable } from "@angular/core";
import { jwtDecode } from "jwt-decode";

/**
 * Handles token operations including validation, scheduling, and storage
 */
@Injectable({ providedIn: "root" })
export class TokenService {
  private minRefreshInterval = 5 * 1000; // 5 seconds
  private lastTokenStorageKey = "last_token_refresh_time";
  private refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /**
   * Checks if the minimum time interval has passed since the last refresh attempt.
   * This method ensures that refresh requests are not sent too frequently based on a configured time interval.
   *
   * @returns {boolean} True if enough time has passed since the last refresh attempt, allowing a new refresh.
   */
  isRefreshIntervalElapsed(): boolean {
    const lastRefresh =
      Number(localStorage.getItem(this.lastTokenStorageKey)) || 0;
    return Date.now() - lastRefresh >= this.minRefreshInterval;
  }

  /**
   * Updates the last refresh timestamp in storage
   */
  updateLastRefresh(): void {
    localStorage.setItem(this.lastTokenStorageKey, Date.now().toString());
  }

  /**
   * Reset the last refresh timestamp in storage
   */
  resetLastRefresh(): void {
    localStorage.removeItem(this.lastTokenStorageKey);
  }

  /**
   * Schedules a token refresh operation
   * @param callback Function to execute when refresh is due
   * @param expiresInSeconds Time until token expiration in seconds
   */
  scheduleRefresh(callback: () => void, expiresInSeconds: number): void {
    const bufferTime = 35; // 35 seconds buffer
    const refreshTime = Math.max(expiresInSeconds - bufferTime, 1) * 1000;
    console.warn("scheduleRefresh in ", refreshTime / 1000);
    this.clearRefreshTimeout();
    this.refreshTimeoutId = setTimeout(() => {
      callback();
      this.refreshTimeoutId = null;
    }, refreshTime);
  }

  /**
   * Clears any pending refresh timeout
   */
  clearRefreshTimeout(): void {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
  }

  /**
   * Validates JWT token expiration
   * @param token JWT token to validate
   * @returns True if token is valid and not expired
   */
  isValid(token: string): boolean {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp ? Date.now() / 1000 < decoded.exp : false;
    } catch {
      return false;
    }
  }

  /**
   * Calculates remaining validity time for a token
   * @param token JWT token to check
   * @returns Remaining validity time in seconds
   */
  getExpirationSeconds(token: string): number {
    try {
      const decoded = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp ? Math.max(decoded.exp - currentTime, 0) : 0;
    } catch {
      return 0;
    }
  }
}
